import React, { useState, useEffect } from "react";
import { MapPin, Clock, Euro, CheckCircle, Eye, X, Building } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Projets exemples (fallback) affichés si l'API ne retourne rien ou en cas d'erreur
const SAMPLE_PROJECTS = [
  {
    id: "sample-1",
    title: "Rénovation complète d'un appartement",
    type: "Rénovation",
    location: "Eure-et-Loir (28)",
    description:
      "Réhabilitation d'un T3 ancien : redistribution des espaces, isolation intérieure, modernisation de l'installation électrique et mise en valeur des matériaux existants.",
    budget: "35 000 €",
    duration: "3 mois",
    status: "Terminé",
    images: [
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    id: "sample-2",
    title: "Extension ossature bois",
    type: "Extension",
    location: "Essonne (91)",
    description:
      "Création d'une extension lumineuse de 25 m² en ossature bois pour agrandir l'espace de vie et ouvrir sur le jardin.",
    budget: "48 000 €",
    duration: "4 mois",
    status: "En cours",
    images: [
      "https://images.unsplash.com/photo-1600566752359-35792bedcfea?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    id: "sample-3",
    title: "Aménagement combles & isolation",
    type: "Aménagement",
    location: "Loiret (45)",
    description:
      "Transformation de combles perdus en suite parentale : renforcement plancher, isolation performante, création d'une salle d'eau et pose de deux velux.",
    budget: "22 000 €",
    duration: "6 semaines",
    status: "Terminé",
    images: [
      "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    id: "sample-4",
    title: "Réorganisation espace intérieur",
    type: "Rénovation",
    location: "Eure-et-Loir (28)",
    description:
      "Ouverture d'un mur porteur pour cuisine/salon, création d'une verrière acier et optimisation des circulations avec rangements sur-mesure.",
    budget: "18 500 €",
    duration: "8 semaines",
    status: "Terminé",
    images: [
      "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?auto=format&fit=crop&w=1200&q=80",
    ],
  },
];

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filtres des types de projets (Rénovation énergétique retiré, Rénovation rétabli)
  const projectTypes = ["Tous", "Rénovation", "Extension", "Aménagement"]; 

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await axios.get(`${API}/public/projects`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setProjects(response.data);
        } else {
          // API vide -> utiliser les exemples
            setProjects(SAMPLE_PROJECTS);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des projets:", error);
        // En cas d'erreur -> projets exemples
        setProjects(SAMPLE_PROJECTS);
      } finally {
        setIsLoading(false);
      }
    };
    loadProjects();
  }, []);

  const filteredProjects = filter === "Tous" 
    ? projects 
    : projects.filter(project => project.type === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case "Terminé":
        return "bg-green-100 text-green-800";
      case "En cours":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <section id="realisations" className="py-12 relative">
      {/* Image de fond (même structure que la section Services) */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2070&q=80')",
        }}
      ></div>
      {/* Overlay semi-transparent (mêmes paramètres) */}
      <div className="absolute inset-0 bg-white/90"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Mes <span className="text-blue-900">Réalisations</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Découvrez quelques-uns des projets que j'ai eu la chance d'accompagner. 
            Chaque réalisation témoigne de notre engagement qualité.
          </p>

          {/* Filter buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {projectTypes.map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  filter === type
                    ? "bg-blue-900 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des projets...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === "Tous" ? "Aucun projet pour le moment" : `Aucun projet "${filter}"`}
            </h3>
            <p className="text-gray-600">Les projets apparaîtront ici une fois ajoutés.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-slate-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Project image */}
              <div className="h-48 bg-gradient-to-br from-blue-100 to-slate-200 flex items-center justify-center overflow-hidden rounded-t-xl">
                {project.images && project.images.length > 0 ? (
                  <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full flex items-center justify-center" style={{ display: (!project.images || project.images.length === 0) ? 'flex' : 'none' }}>
                  <Building className="w-16 h-16 text-blue-900 opacity-50" />
                </div>
              </div>

              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {project.title}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm">{project.location}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {project.description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <Euro className="w-4 h-4 text-blue-900" />
                    <span className="text-sm text-gray-700">
                      <strong>Budget:</strong> {project.budget}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-blue-900" />
                    <span className="text-sm text-gray-700">
                      <strong>Durée:</strong> {project.duration}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className="text-sm font-medium text-blue-900 bg-blue-50 px-3 py-1 rounded-full">
                    {project.type}
                  </span>
                  <button
                    onClick={() => setSelectedProject(project)}
                    className="flex items-center space-x-2 text-blue-900 hover:text-blue-800 font-medium transition-colors duration-200"
                  >
                    <Eye className="w-4 h-4" />
                    <span>Voir détails</span>
                  </button>
                </div>
              </div>
              </div>
            ))}
          </div>
        )}

        {/* Project detail modal */}
        {selectedProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedProject.title}
                    </h3>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{selectedProject.location}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Project Images Gallery */}
                {selectedProject.images && selectedProject.images.length > 0 ? (
                  <div className="mb-6">
                    <div className="h-64 bg-gradient-to-br from-blue-100 to-slate-200 rounded-lg overflow-hidden mb-4">
                      <img
                        src={selectedProject.images[0]}
                        alt={selectedProject.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                        <Building className="w-20 h-20 text-blue-900 opacity-50" />
                      </div>
                    </div>
                    {selectedProject.images.length > 1 && (
                      <div className="flex space-x-2 overflow-x-auto">
                        {selectedProject.images.slice(1).map((imageUrl, index) => (
                          <img
                            key={index + 1}
                            src={imageUrl}
                            alt={`${selectedProject.title} - Image ${index + 2}`}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0 border border-gray-200 cursor-pointer hover:border-blue-500 transition-colors"
                            onClick={(e) => {
                              // Switch main image
                              const mainImg = e.target.closest('.mb-6').querySelector('img');
                              mainImg.src = imageUrl;
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-slate-200 rounded-lg flex items-center justify-center mb-6">
                    <Building className="w-20 h-20 text-blue-900 opacity-50" />
                  </div>
                )}

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {selectedProject.description}
                </p>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Détails du projet</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>Type:</strong> {selectedProject.type}</p>
                      <p><strong>Budget:</strong> {selectedProject.budget}</p>
                      <p><strong>Durée:</strong> {selectedProject.duration}</p>
                      <p><strong>Statut:</strong> {selectedProject.status}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Services fournis</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Étude de faisabilité</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Suivi de chantier</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Conseil technique</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors duration-200"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 text-center">
          <div className="bg-slate-50 rounded-xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Votre projet mérite le même accompagnement
            </h3>
            <p className="text-gray-600 mb-6">
              Chaque réalisation est le fruit d'une collaboration étroite avec mes clients. 
              Parlons de votre projet !
            </p>
            <button
              onClick={() => {
                const element = document.getElementById("contact");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors duration-200"
            >
              Commencer mon projet
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
// EOF 